/**
 * Store Locator — directory listings are server-rendered;
 * this module powers the map, nearest-store search, and opening-hours panels.
 */
(function() {
  'use strict';

  var storeLocatorMap = null;
  var storeLocatorMarkers = [];
  var activeInfoWindow = null;
  var currentMatchResults = [];
  var resultsPage = 1;
  var lastResultsUserAddress = '';
  var lastSearchOrigin = null;
  var locationSearchActive = false;
  var programmaticViewportChange = false;
  var mapUserGesture = false;
  var boundsSearchBound = false;
  var boundsSearchGeneration = 0;
  var resultsPaginationBound = false;

  // Google Maps Platform Geocoding terms: lat/lng may be cached up to 30 calendar days.
  var CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var CACHE_PREFIX_GEOCODE = 'clogau.sl.geocode.v1.';

  var SL_LINK_CLASS = 'text-base-black underline underline-offset-[3px] decoration-1 hover:decoration-2';

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isDebugEnabled() {
    var data = window.__storeLocatorData__ || {};
    return !!(data.config && data.config.debugEnabled);
  }

  function debug(msg, data, clear) {
    if (!isDebugEnabled()) return;
    if (typeof window.__storeLocatorDebug__ === 'function') {
      window.__storeLocatorDebug__(msg, data, clear);
    }
  }

  function storageAvailable() {
    try {
      var key = '__sl_cache_test__';
      window.localStorage.setItem(key, '1');
      window.localStorage.removeItem(key);
      return true;
    } catch (err) {
      return false;
    }
  }

  function readPersistentCache(key) {
    if (!storageAvailable()) return null;
    try {
      var raw = window.localStorage.getItem(key);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || !entry.cachedAt || entry.data == null) {
        window.localStorage.removeItem(key);
        return null;
      }
      if (Date.now() - entry.cachedAt > CACHE_TTL_MS) {
        window.localStorage.removeItem(key);
        return null;
      }
      return entry.data;
    } catch (err) {
      return null;
    }
  }

  function writePersistentCache(key, data) {
    if (!storageAvailable()) return;
    try {
      window.localStorage.setItem(key, JSON.stringify({
        cachedAt: Date.now(),
        ttlMs: CACHE_TTL_MS,
        data: data
      }));
    } catch (err) {
      debug('localStorage write failed', { message: err && err.message, key: key });
    }
  }

  function purgeExpiredCache() {
    if (!storageAvailable()) return;
    try {
      var keys = [];
      for (var i = 0; i < window.localStorage.length; i += 1) {
        var key = window.localStorage.key(i);
        if (key && key.indexOf(CACHE_PREFIX_GEOCODE) === 0) {
          keys.push(key);
        }
      }
      var removed = 0;
      keys.forEach(function(key) {
        var raw = window.localStorage.getItem(key);
        if (!raw) return;
        try {
          var entry = JSON.parse(raw);
          if (!entry || !entry.cachedAt || Date.now() - entry.cachedAt > CACHE_TTL_MS) {
            window.localStorage.removeItem(key);
            removed += 1;
          }
        } catch (err) {
          window.localStorage.removeItem(key);
          removed += 1;
        }
      });
      if (removed) {
        debug('Purged expired store-locator cache entries', { removed: removed });
      }
    } catch (err) {
      debug('Cache purge failed', { message: err && err.message });
    }
  }

  function getData() {
    return window.__storeLocatorData__ || { stores: [], config: {} };
  }

  function normalizeCategory(type, category) {
    if (category) return category;
    var upper = (type || '').toUpperCase();
    if (upper.indexOf('BOUTIQUE') >= 0) return 'boutique';
    if (upper.indexOf('OUTLET') >= 0) return 'outlet';
    if (upper.indexOf('STOCKIST') >= 0) return 'stockist';
    return 'other';
  }

  function buildAddressLines(store) {
    var lines = [];
    if (store.addressLine1) lines.push(store.addressLine1);
    if (store.addressLine2) lines.push(store.addressLine2);
    if (store.city) lines.push(store.city);
    if (store.postcode) lines.push(store.postcode);
    return lines;
  }

  function buildAddressText(store) {
    return buildAddressLines(store).join(', ');
  }

  function buildDisplayName(store) {
    if (store.name) return store.name;
    var label = store.locationLabel || store.city || 'Store';
    return 'Clogau ' + label;
  }

  function haversineDistance(lat1, lon1, lat2, lon2) {
    var R = 3959;
    var dLat = (lat2 - lat1) * Math.PI / 180;
    var dLon = (lon2 - lon1) * Math.PI / 180;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function geocodeAddress(address) {
    var cacheKey = CACHE_PREFIX_GEOCODE + String(address || '').trim().toLowerCase();
    var cached = readPersistentCache(cacheKey);
    if (cached && cached.lat != null && cached.lng != null) {
      debug('Geocode cache hit', { address: address });
      return Promise.resolve(cached);
    }

    return new Promise(function(resolve, reject) {
      var geocoder = new google.maps.Geocoder();
      geocoder.geocode({ address: address }, function(results, status) {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          var loc = results[0].geometry.location;
          var origin = {
            lat: loc.lat(),
            lng: loc.lng(),
            formatted: results[0].formatted_address
          };
          writePersistentCache(cacheKey, origin);
          resolve(origin);
        } else {
          debug('Geocode failed', { status: status, address: address });
          reject(new Error('Could not find that location. Please check and try again.'));
        }
      });
    });
  }

  function hasShopifyAddress(store) {
    return !!(store.addressLine1 || store.addressLine2 || store.city || store.postcode);
  }

  function parseAddressLinesFromFormatted(formatted) {
    if (!formatted) return [];
    return formatted.split(',').map(function(part) {
      return part.trim();
    }).filter(Boolean);
  }

  function getDirectionsUrl(address, placeId) {
    if (address) {
      return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(address);
    }
    if (placeId) {
      return 'https://www.google.com/maps/dir/?api=1&destination_place_id=' + encodeURIComponent(placeId);
    }
    return '';
  }

  function setDirectoryLoading(loading) {
    var panels = document.querySelectorAll('[data-directory-panel]');
    var label = document.querySelector('[data-directory-loading-label]');

    panels.forEach(function(panel) {
      var skeletons = panel.querySelector('[data-directory-skeletons]');
      var cards = panel.querySelector('[data-directory-cards]');
      panel.setAttribute('aria-busy', loading ? 'true' : 'false');
      if (skeletons) {
        var hasSkeletonItems = skeletons.children.length > 0;
        skeletons.hidden = !loading || !hasSkeletonItems;
      }
      if (cards) cards.hidden = !!loading;
    });

    if (label) {
      label.textContent = loading ? 'Loading store directory…' : '';
    }
  }

  function hydrateDirectoryCards() {
    var storesById = {};
    (getData().stores || []).forEach(function(store) {
      if (store && store.id != null) storesById[String(store.id)] = store;
    });

    var cards = Array.prototype.slice.call(
      document.querySelectorAll('.store-locator-directory-card[data-store-id]')
    );

    var boundHours = 0;
    cards.forEach(function(card) {
      var store = storesById[String(card.dataset.storeId)];
      if (!store) return;
      var entry = buildStoreEntry(store);
      if (entry.openingHours) {
        bindHoursToggle(card, entry.openingHours, entry.utcOffsetMinutes);
        boundHours += 1;
      }
    });

    debug('Directory cards hydrated from metaobject data', {
      total: cards.length,
      withHours: boundHours
    });
    setDirectoryLoading(false);
  }

  function normalizeUtcOffsetMinutes(value) {
    if (value == null || value === '') return null;
    var n = parseFloat(value);
    return isNaN(n) ? null : n;
  }

  function hydrateOpeningHoursField(raw) {
    if (!raw) return null;
    var hours = raw;
    if (typeof raw === 'string') {
      if (!raw.trim()) return null;
      try {
        hours = JSON.parse(raw);
      } catch (err) {
        return null;
      }
    }
    if (!hours || typeof hours !== 'object' || Array.isArray(hours)) return null;
    if (!hours.weekday_text && !hours.periods) return null;
    return hours;
  }

  function buildStoreEntry(store) {
    var entry = {
      id: store.id,
      name: buildDisplayName(store),
      category: normalizeCategory(store.type, store.category),
      type: store.type || '',
      phone: store.phone || '',
      email: store.email || '',
      address: buildAddressText(store),
      addressLines: buildAddressLines(store),
      lat: store.lat != null ? parseFloat(store.lat) : null,
      lng: store.lng != null ? parseFloat(store.lng) : null,
      placeId: store.google_places_id || '',
      website: store.website || '',
      businessStatus: store.businessStatus || '',
      utcOffsetMinutes: normalizeUtcOffsetMinutes(store.utcOffsetMinutes),
      openingHours: hydrateOpeningHoursField(store.openingHours),
      distance: null,
      imageUrl: store.imageUrl || null,
      imageAlt: store.imageAlt || ''
    };

    if (!hasShopifyAddress(store) && store.formattedAddress) {
      entry.address = store.formattedAddress;
      entry.addressLines = parseAddressLinesFromFormatted(store.formattedAddress);
    }

    return entry;
  }

  function searchableStores(stores) {
    return stores.filter(function(store) {
      var category = normalizeCategory(store.type, store.category);
      if (category === 'other') return false;
      if (store.businessStatus === 'CLOSED_PERMANENTLY') return false;
      return true;
    });
  }

  function initDirectoryAccordions() {
    var groups = document.querySelectorAll('.js-store-locator-directory-group');
    if (!groups.length) return;

    var desktopQuery = window.matchMedia('(min-width: 1024px)');

    function syncGroups() {
      groups.forEach(function(group) {
        if (desktopQuery.matches) {
          group.setAttribute('open', '');
        } else if (!group.dataset.userToggled) {
          group.removeAttribute('open');
        }
      });
    }

    groups.forEach(function(group) {
      group.addEventListener('toggle', function() {
        if (!desktopQuery.matches) {
          group.dataset.userToggled = 'true';
        }
      });
    });

    if (typeof desktopQuery.addEventListener === 'function') {
      desktopQuery.addEventListener('change', syncGroups);
    } else if (typeof desktopQuery.addListener === 'function') {
      desktopQuery.addListener(syncGroups);
    }

    syncGroups();
  }

  function initMap(config) {
    var mapEl = document.getElementById('store-locator-map');
    if (!mapEl || typeof google === 'undefined') return null;

    storeLocatorMap = new google.maps.Map(mapEl, {
      center: config.mapCenter || { lat: 52.5, lng: -2.5 },
      zoom: config.mapZoom || 6,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true
    });

    activeInfoWindow = new google.maps.InfoWindow();
    storeLocatorMap.addListener('click', function() {
      closeActiveInfoWindow();
    });

    return storeLocatorMap;
  }

  function closeActiveInfoWindow() {
    if (activeInfoWindow) {
      activeInfoWindow.close();
    }
  }

  function clearMarkers() {
    closeActiveInfoWindow();
    storeLocatorMarkers.forEach(function(marker) {
      marker.setMap(null);
    });
    storeLocatorMarkers = [];
  }

  function renderMapMarkers(stores, options) {
    if (!storeLocatorMap || typeof google === 'undefined') return;

    var opts = options || {};
    var focusOrigin = opts.focusOrigin;
    var hasFocusOrigin = !!(
      focusOrigin &&
      focusOrigin.lat != null &&
      focusOrigin.lng != null
    );
    var storesWithCoords = stores.filter(function(store) {
      return store.lat != null && store.lng != null;
    });

    clearMarkers();

    if (!storesWithCoords.length && !hasFocusOrigin) return;

    var bounds = new google.maps.LatLngBounds();

    if (!activeInfoWindow) {
      activeInfoWindow = new google.maps.InfoWindow();
    }

    storesWithCoords.forEach(function(store) {
      var pos = { lat: store.lat, lng: store.lng };
      var marker = new google.maps.Marker({
        position: pos,
        map: storeLocatorMap,
        title: store.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: '#000000',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 8
        }
      });

      marker.addListener('click', function() {
        activeInfoWindow.setContent(
          '<div style="padding:8px;max-width:220px;"><strong>' +
            escapeHtml(store.name || 'Store') + '</strong><br>' + escapeHtml(store.address || '') + '</div>'
        );
        activeInfoWindow.open(storeLocatorMap, marker);
      });

      storeLocatorMarkers.push(marker);
      bounds.extend(pos);
    });

    if (hasFocusOrigin) {
      programmaticViewportChange = true;
      mapUserGesture = false;
      storeLocatorMap.setCenter({ lat: focusOrigin.lat, lng: focusOrigin.lng });
      storeLocatorMap.setZoom(opts.centerZoom != null ? opts.centerZoom : 11);
    } else if (opts.fitToMarkers !== false && storesWithCoords.length) {
      programmaticViewportChange = true;
      mapUserGesture = false;
      if (storesWithCoords.length === 1) {
        storeLocatorMap.setCenter({ lat: storesWithCoords[0].lat, lng: storesWithCoords[0].lng });
        storeLocatorMap.setZoom(opts.singleZoom || 12);
      } else {
        storeLocatorMap.fitBounds(bounds, 48);
      }
    }

    setTimeout(function() {
      if (storeLocatorMap && google.maps.event) {
        google.maps.event.trigger(storeLocatorMap, 'resize');
      }
    }, 100);
  }

  function bindMapBoundsSearch() {
    if (!storeLocatorMap || boundsSearchBound || typeof google === 'undefined') return;
    boundsSearchBound = true;

    storeLocatorMap.addListener('dragend', function() {
      if (!locationSearchActive || programmaticViewportChange) return;
      mapUserGesture = true;
    });

    storeLocatorMap.addListener('zoom_changed', function() {
      if (!locationSearchActive || programmaticViewportChange) return;
      mapUserGesture = true;
    });

    storeLocatorMap.addListener('idle', function() {
      if (programmaticViewportChange) {
        programmaticViewportChange = false;
        mapUserGesture = false;
        return;
      }
      if (!locationSearchActive || !mapUserGesture) return;
      mapUserGesture = false;
      runBoundsSearch();
    });
  }

  function formatHoursTable(openingHours) {
    if (!openingHours || !openingHours.weekday_text || !openingHours.weekday_text.length) {
      return [];
    }

    var dayAbbrev = {
      monday: 'MON',
      tuesday: 'TUE',
      wednesday: 'WED',
      thursday: 'THU',
      friday: 'FRI',
      saturday: 'SAT',
      sunday: 'SUN'
    };

    return openingHours.weekday_text.map(function(line) {
      var parts = line.split(':');
      var dayName = (parts.shift() || '').trim().toLowerCase();
      var abbrev = dayAbbrev[dayName] || dayName.slice(0, 3).toUpperCase();
      var hours = parts.join(':').trim()
        .replace(/\u2013|\u2014/g, '-')
        .replace(/(\d)\s*(AM|PM)/gi, '$1$2')
        .replace(/\s*-\s*/g, ' - ');
      return { day: abbrev + ':', hours: hours };
    });
  }

  function computeOpenNow(periods, utcOffsetMinutes) {
    if (!periods || !periods.length || utcOffsetMinutes == null) return null;
    var now = new Date(Date.now() + utcOffsetMinutes * 60000);
    var t = function (p) {
      if (!p || p.time == null) return null;
      return parseInt(p.time.slice(0, 2), 10) * 60 + parseInt(p.time.slice(2, 4) || '0', 10);
    };
    var cur = now.getUTCDay() * 1440 + now.getUTCHours() * 60 + now.getUTCMinutes();
    for (var i = 0; i < periods.length; i++) {
      var p = periods[i];
      if (!p.open || p.open.day == null) continue;
      var start = p.open.day * 1440 + t(p.open);
      var closeDay = p.close && p.close.day != null ? p.close.day : p.open.day;
      var end = closeDay * 1440 + (t(p.close) != null ? t(p.close) : 1440);
      if (end <= start) end += 7 * 1440;                       // overnight span
      if ((cur >= start && cur < end) || (cur + 7 * 1440 >= start && cur + 7 * 1440 < end)) return true;
    }
    return false;
  }

  function renderOpenStatus(el, openingHours, utcOffsetMinutes) {
    if (!el || !openingHours) return;

    var isOpen = computeOpenNow(openingHours.periods, utcOffsetMinutes);
    if (isOpen == null) {
      el.hidden = true;
      return;
    }

    el.textContent = isOpen ? 'Open now' : 'Closed now';
    el.classList.toggle('is-closed', !isOpen);
    el.hidden = false;
  }

  function renderHoursTable(container, openingHours) {
    if (!container) return;
    container.innerHTML = '';

    var rows = formatHoursTable(openingHours);
    rows.forEach(function(row) {
      var dt = document.createElement('dt');
      dt.textContent = row.day;
      var dd = document.createElement('dd');
      dd.textContent = row.hours;
      container.appendChild(dt);
      container.appendChild(dd);
    });
  }

  function bindHoursToggle(item, openingHours, utcOffsetMinutes) {
    var toggle = item.querySelector('[data-field="hours-toggle"]');
    var panel = item.querySelector('[data-field="hours-panel"]');
    if (!toggle || !panel || !openingHours) return;

    toggle.hidden = false;
    toggle.addEventListener('click', function() {
      var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      var nextExpanded = !isExpanded;
      toggle.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
      panel.hidden = !nextExpanded;
      toggle.querySelector('span').textContent = nextExpanded ? 'Hide Opening Hours' : 'View Opening Hours';
      if (nextExpanded) {
        renderOpenStatus(item.querySelector('[data-field="open-status"]'), openingHours, utcOffsetMinutes);
        renderHoursTable(item.querySelector('[data-field="hours-table"]'), openingHours);
      }
    });
  }

  function getResultsPageSize() {
    var config = getData().config || {};
    var size = parseInt(config.maxResults, 10);
    return size > 0 ? size : 10;
  }

  function hideResultsPagination() {
    var paginationEl = document.getElementById('store-locator-results-pagination');
    if (paginationEl) paginationEl.hidden = true;
  }

  function updateResultsPagination() {
    var paginationEl = document.getElementById('store-locator-results-pagination');
    var prevBtn = document.getElementById('store-locator-results-prev');
    var nextBtn = document.getElementById('store-locator-results-next');
    var statusEl = document.getElementById('store-locator-results-status');
    if (!paginationEl || !prevBtn || !nextBtn || !statusEl) return;

    var total = currentMatchResults.length;
    var pageSize = getResultsPageSize();
    var totalPages = Math.max(1, Math.ceil(total / pageSize));

    if (total <= pageSize) {
      paginationEl.hidden = true;
      return;
    }

    if (resultsPage > totalPages) resultsPage = totalPages;
    if (resultsPage < 1) resultsPage = 1;

    var from = (resultsPage - 1) * pageSize + 1;
    var to = Math.min(resultsPage * pageSize, total);

    paginationEl.hidden = false;
    statusEl.textContent = 'Showing ' + from + '\u2013' + to + ' of ' + total;
    prevBtn.disabled = resultsPage <= 1;
    nextBtn.disabled = resultsPage >= totalPages;
    prevBtn.setAttribute('aria-disabled', prevBtn.disabled ? 'true' : 'false');
    nextBtn.setAttribute('aria-disabled', nextBtn.disabled ? 'true' : 'false');
  }

  function bindResultsPagination() {
    if (resultsPaginationBound) return;
    var prevBtn = document.getElementById('store-locator-results-prev');
    var nextBtn = document.getElementById('store-locator-results-next');
    if (!prevBtn || !nextBtn) return;

    resultsPaginationBound = true;

    prevBtn.addEventListener('click', function() {
      if (resultsPage <= 1) return;
      resultsPage -= 1;
      renderSearchResultsPage();
    });

    nextBtn.addEventListener('click', function() {
      var pageSize = getResultsPageSize();
      var totalPages = Math.max(1, Math.ceil(currentMatchResults.length / pageSize));
      if (resultsPage >= totalPages) return;
      resultsPage += 1;
      renderSearchResultsPage();
    });
  }

  function renderSearchResultsPage() {
    var listEl = document.getElementById('store-locator-search-results');
    var placeholderEl = document.getElementById('store-locator-search-placeholder');
    var template = document.getElementById('store-locator-search-item-template');
    if (!listEl || !template) return;

    listEl.innerHTML = '';
    setLoading(false);

    if (!currentMatchResults.length) {
      listEl.hidden = true;
      hideResultsPagination();
      return;
    }

    listEl.hidden = false;
    if (placeholderEl) placeholderEl.hidden = true;

    var pageSize = getResultsPageSize();
    var totalPages = Math.max(1, Math.ceil(currentMatchResults.length / pageSize));
    if (resultsPage > totalPages) resultsPage = totalPages;
    if (resultsPage < 1) resultsPage = 1;

    var start = (resultsPage - 1) * pageSize;
    var pageStores = currentMatchResults.slice(start, start + pageSize);
    var userAddress = lastResultsUserAddress;

    pageStores.forEach(function(store) {
      var frag = template.content.cloneNode(true);
      var item = frag.querySelector('.store-locator-search-item');

      item.querySelector('[data-field="name"]').textContent = (store.name || 'Store').toUpperCase();

      var imageWrap = item.querySelector('[data-field="image-wrap"]');
      var imageEl = item.querySelector('[data-field="image"]');
      if (store.imageUrl && imageWrap && imageEl) {
        imageEl.src = store.imageUrl;
        imageEl.alt = store.imageAlt || store.name || '';
        imageWrap.hidden = false;
      }

      var addressEl = item.querySelector('[data-field="address"]');
      if (store.addressLines && store.addressLines.length) {
        addressEl.innerHTML = store.addressLines.map(function(line) {
          return escapeHtml(line);
        }).join('<br>');
      } else {
        addressEl.textContent = store.address || '';
      }

      var phoneWrap = item.querySelector('[data-field="phone-wrap"]');
      var phoneLink = item.querySelector('[data-field="phone-link"]');
      if (store.phone && phoneWrap && phoneLink) {
        phoneLink.href = 'tel:' + store.phone.replace(/\s/g, '');
        phoneLink.textContent = store.phone;
        phoneWrap.hidden = false;
      }

      var emailWrap = item.querySelector('[data-field="email-wrap"]');
      var emailLink = item.querySelector('[data-field="email-link"]');
      if (store.email && emailWrap && emailLink) {
        emailLink.href = 'mailto:' + store.email;
        emailLink.textContent = store.email;
        emailWrap.hidden = false;
      }

      var directionsLink = item.querySelector('[data-field="directions-link"]');
      var destination = store.address || '';
      if (directionsLink && (destination || store.placeId)) {
        var origin = userAddress ? '&saddr=' + encodeURIComponent(userAddress) : '';
        directionsLink.href = getDirectionsUrl(destination, store.placeId) + origin;
        directionsLink.hidden = false;
      }

      bindHoursToggle(item, store.openingHours, store.utcOffsetMinutes);
      listEl.appendChild(item);
    });

    updateResultsPagination();
  }

  function renderSearchResults(stores, userAddress, options) {
    var listEl = document.getElementById('store-locator-search-results');
    var placeholderEl = document.getElementById('store-locator-search-placeholder');
    if (!listEl) return;

    var opts = options || {};
    currentMatchResults = stores || [];
    lastResultsUserAddress = userAddress || '';
    resultsPage = 1;
    setLoading(false);

    if (!currentMatchResults.length) {
      listEl.innerHTML = '';
      listEl.hidden = true;
      hideResultsPagination();
      if (placeholderEl) {
        placeholderEl.hidden = false;
        if (opts.initial) {
          placeholderEl.textContent = placeholderEl.dataset.defaultText || placeholderEl.textContent;
        } else {
          placeholderEl.textContent = 'No stores found. Try changing your filters or search location.';
        }
      }
      return;
    }

    renderSearchResultsPage();
  }

  function getActiveFilters() {
    var filters = [];
    document.querySelectorAll('[data-store-locator-filter]:checked').forEach(function(input) {
      filters.push(input.value);
    });
    return filters;
  }

  function getSearchRadius() {
    return 0;
  }

  function showError(message) {
    var errEl = document.getElementById('store-locator-error');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.hidden = false;
  }

  function hideError() {
    var errEl = document.getElementById('store-locator-error');
    if (errEl) errEl.hidden = true;
  }

  function setLoading(loading) {
    var loadingEl = document.getElementById('store-locator-loading');
    var skeletonEl = document.getElementById('store-locator-skeletons');
    var mapSkeletonEl = document.getElementById('store-locator-map-skeleton');
    var resultsEl = document.getElementById('store-locator-search-results');
    var placeholderEl = document.getElementById('store-locator-search-placeholder');
    var panelEl = document.querySelector('[data-store-locator-results-panel]');

    if (loadingEl) {
      loadingEl.hidden = !loading;
      loadingEl.textContent = loading ? 'Finding your nearest stores…' : '';
    }

    if (skeletonEl) skeletonEl.hidden = !loading;
    if (mapSkeletonEl) mapSkeletonEl.hidden = !loading;
    if (panelEl) panelEl.setAttribute('aria-busy', loading ? 'true' : 'false');

    if (loading) {
      if (resultsEl) resultsEl.hidden = true;
      if (placeholderEl) placeholderEl.hidden = true;
      hideResultsPagination();
    }
  }

  function hideMapSkeleton() {
    var mapSkeletonEl = document.getElementById('store-locator-map-skeleton');
    if (mapSkeletonEl) mapSkeletonEl.hidden = true;
  }

  function filterStoresByOrigin(enriched, origin, filters, radius) {
    return enriched
      .filter(function(store) {
        return filters.indexOf(store.category) >= 0;
      })
      .map(function(store) {
        if (store.lat != null && store.lng != null && origin) {
          store.distance = haversineDistance(origin.lat, origin.lng, store.lat, store.lng);
        } else {
          store.distance = null;
        }
        return store;
      })
      .filter(function(store) {
        if (!radius) return true;
        return store.distance != null && store.distance <= radius;
      })
      .sort(function(a, b) {
        if (a.distance == null && b.distance == null) return 0;
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
      });
  }

  function filterStoresByBounds(enriched, bounds, origin, filters) {
    return enriched
      .filter(function(store) {
        return filters.indexOf(store.category) >= 0;
      })
      .filter(function(store) {
        if (store.lat == null || store.lng == null) return false;
        return bounds.contains(new google.maps.LatLng(store.lat, store.lng));
      })
      .map(function(store) {
        if (origin && origin.lat != null && origin.lng != null) {
          store.distance = haversineDistance(origin.lat, origin.lng, store.lat, store.lng);
        } else {
          store.distance = null;
        }
        return store;
      })
      .sort(function(a, b) {
        if (a.distance == null && b.distance == null) return 0;
        if (a.distance == null) return 1;
        if (b.distance == null) return -1;
        return a.distance - b.distance;
      });
  }

  function runBoundsSearch() {
    if (!storeLocatorMap || !locationSearchActive || typeof google === 'undefined') {
      return;
    }

    var bounds = storeLocatorMap.getBounds();
    if (!bounds) return;

    var data = getData();
    var candidates = searchableStores(data.stores || []);
    var filters = getActiveFilters();

    if (!filters.length) {
      showError('Please select at least one store type to search.');
      return;
    }

    hideError();
    setLoading(true);

    var generation = ++boundsSearchGeneration;
    var centre = storeLocatorMap.getCenter();
    var sortOrigin = centre
      ? {
          lat: centre.lat(),
          lng: centre.lng(),
          formatted: (lastSearchOrigin && lastSearchOrigin.formatted) || ''
        }
      : lastSearchOrigin;

    debug('Bounds search started', {
      filters: filters,
      generation: generation,
      ne: bounds.getNorthEast().toJSON(),
      sw: bounds.getSouthWest().toJSON()
    });

    try {
      var enriched = candidates.map(buildStoreEntry);

      var results = filterStoresByBounds(enriched, bounds, sortOrigin, filters);
      var mapResults = results.filter(function(store) {
        return store.lat != null && store.lng != null;
      });

      debug('Bounds search results', {
        matched: results.length,
        mapCount: mapResults.length,
        generation: generation
      });

      renderSearchResults(results, sortOrigin && sortOrigin.formatted, { initial: false });
      renderMapMarkers(mapResults, { fitToMarkers: false });

      if (!results.length) {
        showError('No stores found. Try changing your filters or search location.');
      }
    } catch (err) {
      debug('Bounds search error', { message: err && err.message });
      showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function runSearch(origin, options) {
    var data = getData();
    var config = data.config || {};
    var opts = options || {};
    var isInitial = !!opts.isInitial;
    var isBrowseMode = isInitial || !locationSearchActive;
    var candidates = searchableStores(data.stores || []);
    var filters = opts.filters || getActiveFilters();
    var radius = opts.radius != null ? opts.radius : getSearchRadius();
    var pageSize = config.maxResults || 10;

    if (!filters.length) {
      showError('Please select at least one store type to search.');
      return;
    }

    hideError();
    setLoading(true);
    ++boundsSearchGeneration;
    debug(isInitial ? 'Initial search started' : (isBrowseMode ? 'Browse search started' : 'Search started'), {
      origin: origin,
      filters: filters,
      radius: radius,
      pageSize: pageSize,
      locationSearchActive: locationSearchActive,
      browseMode: isBrowseMode
    }, !isInitial);

    try {
      var enriched = candidates.map(buildStoreEntry);

      var results = filterStoresByOrigin(enriched, origin, filters, radius);
      var mapResults = results.filter(function(store) {
        return store.lat != null && store.lng != null;
      });

      debug('Search results', {
        matched: results.length,
        listPageSize: pageSize,
        mapCount: mapResults.length,
        stores: results.map(function(s) {
          return {
            name: s.name,
            distance: s.distance,
            category: s.category,
            hasCoords: s.lat != null && s.lng != null
          };
        })
      });

      if (!mapResults.length) {
        debug('No mappable stores in results', {
          reason: results.length
            ? 'Matched stores have no lat/lng in embedded data'
            : 'No stores matched filters/radius',
          filters: filters,
          radius: radius
        });
      }

      renderSearchResults(results, origin && origin.formatted, { initial: isInitial });

      var markerOpts = {
        fitToMarkers: true,
        singleZoom: isBrowseMode ? 6 : 11
      };
      if (!isBrowseMode && origin && origin.lat != null && origin.lng != null) {
        markerOpts.focusOrigin = { lat: origin.lat, lng: origin.lng };
        markerOpts.centerZoom = 11;
      }
      renderMapMarkers(mapResults, markerOpts);

      if (!results.length && !isInitial) {
        showError('No stores found. Try changing your filters or search location.');
      }

      return results;
    } catch (err) {
      debug('Search error', { message: err && err.message });
      showError(err && err.message ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function runDefaultSearch() {
    var config = getData().config || {};
    locationSearchActive = false;

    if (config.defaultSearchOrigin && config.defaultSearchOrigin.lat != null && config.defaultSearchOrigin.lng != null) {
      var origin = {
        lat: parseFloat(config.defaultSearchOrigin.lat),
        lng: parseFloat(config.defaultSearchOrigin.lng),
        formatted: config.defaultSearchLocation || ''
      };
      lastSearchOrigin = origin;
      debug('Default search origin resolved from settings', origin);
      return runSearch(origin, {
        isInitial: true,
        radius: config.defaultSearchRadius != null ? config.defaultSearchRadius : 0,
        filters: getActiveFilters()
      });
    }

    var location = config.defaultSearchLocation;
    if (!location) {
      debug('Default search skipped', { reason: 'No default search origin or location configured' });
      return;
    }

    return geocodeAddress(location)
      .then(function(origin) {
        lastSearchOrigin = origin;
        debug('Default search origin resolved', origin);
        return runSearch(origin, {
          isInitial: true,
          radius: config.defaultSearchRadius != null ? config.defaultSearchRadius : 0,
          filters: getActiveFilters()
        });
      })
      .catch(function(err) {
        debug('Default search failed', { message: err && err.message, location: location });
        var filters = getActiveFilters();
        var mapStores = searchableStores(getData().stores || [])
          .map(buildStoreEntry)
          .filter(function(store) {
            return store.lat != null && store.lng != null && filters.indexOf(store.category) >= 0;
          });
        renderMapMarkers(mapStores, { fitToMarkers: true, singleZoom: 6 });
        if (!mapStores.length) {
          debug('Fallback map empty', {
            reason: 'No stores with coordinates in embedded data'
          });
        }
      });
  }

  function mapsApiReady() {
    return typeof google !== 'undefined' && google.maps && google.maps.Geocoder;
  }

  function waitForGoogleMaps(config, options) {
    var opts = options || {};
    var timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 15000;
    var intervalMs = opts.intervalMs != null ? opts.intervalMs : 100;

    return new Promise(function(resolve, reject) {
      if (!config.apiKey) {
        reject(new Error('Missing API key'));
        return;
      }

      if (mapsApiReady()) {
        resolve();
        return;
      }

      var elapsed = 0;
      debug('Waiting for Google Maps…', { timeoutMs: timeoutMs });

      var timer = setInterval(function() {
        elapsed += intervalMs;
        if (mapsApiReady()) {
          clearInterval(timer);
          debug('Google Maps ready', { waitedMs: elapsed });
          resolve();
          return;
        }
        if (elapsed >= timeoutMs) {
          clearInterval(timer);
          reject(new Error('Google Maps failed to load within ' + timeoutMs + 'ms'));
        }
      }, intervalMs);
    });
  }

  function startMapFeatures(config, data) {
    if (!mapsApiReady()) {
      debug('Google Maps JS failed to load', {
        hint: 'Check API key, Maps JavaScript API enablement, Geocoding API, billing, and HTTP referrer restrictions'
      });
      setDirectoryLoading(false);
      setLoading(false);
      var placeholderEl = document.getElementById('store-locator-search-placeholder');
      if (placeholderEl) placeholderEl.hidden = false;
      return;
    }

    setDirectoryLoading(true);
    setLoading(true);
    initMap(config);
    bindMapBoundsSearch();
    hideMapSkeleton();
    hydrateDirectoryCards();
    runDefaultSearch();
  }

  window.initStoreLocator = function() {
    var form = document.getElementById('store-locator-form');
    if (!form || form.dataset.storeLocatorInit === 'true') return;
    form.dataset.storeLocatorInit = 'true';

    var data = getData();
    var config = data.config || {};

    initDirectoryAccordions();
    purgeExpiredCache();
    setDirectoryLoading(true);
    setLoading(true);
    bindResultsPagination();

    form.addEventListener('submit', function(e) {
      e.preventDefault();

      if (!config.apiKey) {
        showError('Store locator search requires a Google Maps API key in the theme settings.');
        return;
      }

      if (!mapsApiReady()) {
        showError('Google Maps is still loading. Please try again in a moment.');
        debug('Search blocked: Google Maps still loading', null);
        return;
      }

      var input = document.getElementById('store-locator-postcode');
      var query = input && input.value ? input.value.trim() : '';
      if (!query) {
        showError('Please enter a postcode or address.');
        return;
      }

      geocodeAddress(query)
        .then(function(origin) {
          lastSearchOrigin = origin;
          locationSearchActive = true;
          return runSearch(origin);
        })
        .catch(function(err) {
          showError(err && err.message ? err.message : 'Could not find that location.');
        });
    });

    document.querySelectorAll('[data-store-locator-filter]').forEach(function(el) {
      el.addEventListener('change', function() {
        if (lastSearchOrigin) {
          runSearch(lastSearchOrigin);
        } else {
          runDefaultSearch();
        }
      });
    });

    if (!config.apiKey) {
      debug('Missing API key', {
        hint: 'Add a Google Maps API key in the Store Locator section settings'
      });
      setDirectoryLoading(false);
      setLoading(false);
      var placeholderEl = document.getElementById('store-locator-search-placeholder');
      if (placeholderEl) placeholderEl.hidden = false;
      return;
    }

    waitForGoogleMaps(config)
      .then(function() {
        startMapFeatures(config, data);
      })
      .catch(function(err) {
        debug('Google Maps JS failed to load', {
          message: err && err.message,
          hint: 'Check API key, Maps JavaScript API enablement, Geocoding API, billing, and HTTP referrer restrictions'
        });
        setDirectoryLoading(false);
        setLoading(false);
        var placeholderEl = document.getElementById('store-locator-search-placeholder');
        if (placeholderEl) placeholderEl.hidden = false;
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', window.initStoreLocator);
  } else {
    window.initStoreLocator();
  }
})();
